import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { ErrorState } from "@arcadeai/design-system/components/ui/molecules/error-state";
import { captureException, withScope } from "@sentry/react";
import { Link, useRouter } from "@tanstack/react-router";
import { Home, RotateCcw } from "lucide-react";
import { useEffect } from "react";
import { isAuthRecoveryHttpError } from "@/lib/http-error";

export function RouteErrorBoundary({ error }: { error: Error }) {
  const router = useRouter();
  // Suppress only auth-recovery 401s — sendRequest opens the overlay for
  // those. All other 401s (engine, coordinator) render the error UI here.
  const isAuthError = isAuthRecoveryHttpError(error);

  useEffect(() => {
    if (isAuthError) {
      return;
    }
    withScope((scope) => {
      scope.setTag("errorBoundary", "tanstack-router");
      scope.setLevel("error");
      scope.setContext("routeError", {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        timestamp: new Date().toISOString(),
      });
      captureException(error);
    });
  }, [error, isAuthError]);

  if (isAuthError) {
    return null;
  }

  return (
    <ErrorState
      errorMessage={error.message || "An unknown error occurred"}
      errorStack={error.stack}
    >
      <Button
        className="flex-1"
        onClick={() => router.invalidate()}
        variant="outline"
      >
        <RotateCcw className="mr-2 h-4 w-4" />
        Try Again
      </Button>
      <Button className="flex-1" render={<Link to="/" />}>
        <Home className="mr-2 h-4 w-4" />
        Go Home
      </Button>
    </ErrorState>
  );
}
